import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { defineMessages, useIntl } from 'react-intl';
import { MeApi, PathwaysApi, StreakApi } from 'src/api';
import { Modal } from 'src/components';
import { useUserContext } from 'src/context/user';
import { useEligibility } from 'src/hooks/useEligibility';
import { DEBUG_FORCE_CALENDAR_REMINDER_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { FIVE_MINUTES } from 'src/constants/times.const';
import { twentyFourHoursAgo } from 'src/apps/DashboardFrame/utils/twentyFourHoursAgo';
import { isNotProduction } from 'src/utils/env/isNotProduction';
import { SOURCE_LANGUAGE, TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { getLocalStorageEntry, removeLocalStorageEntry } from 'src/utils/webStorageUtils';
import * as S from '../styles';
import { isEnglishLearningSpanish } from './eligibility';
import { CALENDAR_REMINDER_SESSION_THRESHOLD } from './event';
import { useCalendarSessionCount } from './sessionCompletions';
import { doesNotHaveSeenCalendarReminder } from './storage';
import { CalendarReminder } from '.';

const msgs = defineMessages({
  language: {
    id: 'calendar_reminder_fallback_language',
    defaultMessage: 'your language',
  },
});

type CalendarReminderOptions = {
  languagePairId: number | null;
  pathwayNodeId?: number | null;
  enabled?: boolean;
  finishedAt?: Date;
};

export const useCalendarReminderEligibility = ({
  languagePairId,
  pathwayNodeId = null,
  enabled = true,
  finishedAt,
}: CalendarReminderOptions) => {
  const { user } = useUserContext();
  const { isEligibleForPathways } = useEligibility(languagePairId);
  const finishedAtRef = useRef(finishedAt ?? new Date());
  const [debugForceCalendarReminder] = useState(
    () =>
      isNotProduction() &&
      getLocalStorageEntry(DEBUG_FORCE_CALENDAR_REMINDER_STORAGE_KEY) === 'true',
  );
  const excludedLanguagePair = isEnglishLearningSpanish(
    getLocalStorageEntry(SOURCE_LANGUAGE),
    getLocalStorageEntry(TARGET_LANGUAGE),
  );
  const baseEligible =
    enabled &&
    (debugForceCalendarReminder ||
      (!excludedLanguagePair &&
        !!user?.dateJoined &&
        twentyFourHoursAgo(Date.parse(user.dateJoined)) &&
        isEligibleForPathways &&
        doesNotHaveSeenCalendarReminder(user)));
  const queryEnabled = baseEligible && languagePairId !== null;
  const pathwayQuery = useQuery({
    queryKey: ['activePathway', languagePairId],
    queryFn: () => PathwaysApi.getActivePathway(languagePairId as number),
    enabled: queryEnabled,
    retry: false,
    staleTime: FIVE_MINUTES,
  });
  const sessionsToday = useCalendarSessionCount(
    user?.id,
    languagePairId,
    pathwayQuery.data,
    finishedAtRef.current,
    pathwayNodeId,
  );
  const reachedThreshold = sessionsToday >= CALENDAR_REMINDER_SESSION_THRESHOLD;
  const statsEnabled = queryEnabled && (debugForceCalendarReminder || reachedThreshold);
  const statsQuery = useQuery({
    queryKey: ['calendarReminderStats', languagePairId],
    queryFn: () => MeApi.getStats(languagePairId as number, { daysToCompare: 1 }),
    enabled: statsEnabled,
    retry: false,
    staleTime: FIVE_MINUTES,
  });
  const isLoading =
    queryEnabled &&
    !debugForceCalendarReminder &&
    (pathwayQuery.isLoading || (statsEnabled && statsQuery.isLoading));

  useEffect(() => {
    if (enabled && debugForceCalendarReminder) {
      removeLocalStorageEntry(DEBUG_FORCE_CALENDAR_REMINDER_STORAGE_KEY);
    }
  }, [debugForceCalendarReminder, enabled]);

  return {
    isEligible: baseEligible && (debugForceCalendarReminder || (!isLoading && reachedThreshold)),
    isLoading,
    sessionsToday,
    wordsLearntToday: statsQuery.data?.currentData.wordsLearned ?? 0,
    finishedAt: finishedAtRef.current,
  };
};

export const useCalendarReminderExit = (options: CalendarReminderOptions) => {
  const intl = useIntl();
  const eligibility = useCalendarReminderEligibility(options);
  const [pendingExit, setPendingExit] = useState<(() => void) | null>(null);
  const [showReminder, setShowReminder] = useState(false);
  const { languagePairId } = options;
  const detailsEnabled = showReminder && languagePairId !== null;
  const languages = useQuery({
    queryKey: ['userLanguages'],
    queryFn: () => MeApi.getUserLanguages(),
    enabled: detailsEnabled,
    retry: false,
    staleTime: FIVE_MINUTES,
  });
  const streak = useQuery({
    queryKey: ['streak', languagePairId],
    queryFn: () => StreakApi.getStreak(languagePairId as number),
    enabled: detailsEnabled,
    retry: false,
    staleTime: FIVE_MINUTES,
  });

  const beforeExit = useCallback(
    (proceed: () => void) => {
      if (eligibility.isLoading || eligibility.isEligible) {
        setPendingExit(() => proceed);
        setShowReminder(eligibility.isEligible);
      } else {
        proceed();
      }
    },
    [eligibility.isEligible, eligibility.isLoading],
  );
  useEffect(() => {
    if (!pendingExit || showReminder || eligibility.isLoading) return;
    if (eligibility.isEligible) {
      setShowReminder(true);
    } else {
      setPendingExit(null);
      pendingExit();
    }
  }, [eligibility.isEligible, eligibility.isLoading, pendingExit, showReminder]);

  const finish = () => {
    setShowReminder(false);
    setPendingExit(null);
    pendingExit?.();
  };
  const languagePair = languages.data?.languagePairs.find(
    pair => pair.languagePairId === languagePairId,
  );
  const detailsLoading = showReminder && (languages.isLoading || streak.isLoading);
  const interstitial =
    showReminder && !detailsLoading ? (
      <CalendarReminder
        languageName={languagePair?.targetLanguageName ?? intl.formatMessage(msgs.language)}
        wordsLearntToday={eligibility.wordsLearntToday}
        sessionsToday={eligibility.sessionsToday}
        streakCount={streak.data?.currentStreak.count ?? 0}
        streakAccomplishedToday={!!streak.data?.currentStreak.accomplishedToday}
        finishedAt={eligibility.finishedAt}
        onContinue={finish}
        onDecline={finish}
      />
    ) : pendingExit ? (
      <Modal isOpen fullSize hideCloseButton handleClose={() => {}}>
        <S.LoaderWrapper data-testid="calendar-reminder-pending">
          <S.Loader />
        </S.LoaderWrapper>
      </Modal>
    ) : null;

  return { beforeExit, interstitial };
};
